import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { GetLayoutFontFaces } from "src/shared/ui/src/display-editor";
import {
  DEFAULT_USER_LAYOUT_DISCIPLINE_KEY,
  type LayoutDocument,
  validateLayout,
} from "src/shared/util/src/layout";
import { getWebSocketUrl } from "src/config";
import { useRuntimeMeetLayoutCatalog } from "./useRuntimeMeetLayoutCatalog";

type UseResolvedRuntimeLayoutOptions = {
  meetId?: string;
  token?: string | null;
  targetKey?: string | null;
  disciplineKey?: string | null;
  layoutType: "widget" | "display";
  websocketUrl?: string;
};

type UseResolvedRuntimeLayoutResult = {
  customLayout: LayoutDocument | null;
  currentThemeName: string | null;
  targetLabel: string | null;
  isLoading: boolean;
  error: string | null;
  retry: () => void;
  loadLayoutFontFaces: GetLayoutFontFaces;
};

/**
 * Resolves the concrete runtime layout document matching the current meet
 * theme and a shared `targetKey`.
 *
 * @param options - Meet context, target key and expected layout type.
 * @returns The validated layout document plus loading/error state.
 */
export function useResolvedRuntimeLayout(
  options: UseResolvedRuntimeLayoutOptions,
): UseResolvedRuntimeLayoutResult {
  const {
    meetId,
    targetKey,
    disciplineKey,
    layoutType,
    websocketUrl = getWebSocketUrl(),
  } = options;
  const {
    currentThemeName,
    layoutsByTargetKey,
    fonts,
    isLoading,
    error,
    retry,
  } = useRuntimeMeetLayoutCatalog({
    meetId,
    websocketUrl,
    layoutType,
  });
  const [customLayout, setCustomLayout] = useState<LayoutDocument | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const resolvedLayoutKeyRef = useRef<string | null>(null);

  const matchedLayout = useMemo(() => {
    if (!targetKey) return null;
    const entries = layoutsByTargetKey.get(targetKey) ?? [];
    const normalizedDisciplineKey =
      disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY;
    return (
      entries.find(
        (entry) =>
          (entry.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY) ===
          normalizedDisciplineKey,
      ) ??
      entries.find(
        (entry) =>
          (entry.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY) ===
          DEFAULT_USER_LAYOUT_DISCIPLINE_KEY,
      ) ??
      null
    );
  }, [disciplineKey, layoutsByTargetKey, targetKey]);

  const matchedLayoutKey = matchedLayout
    ? `${matchedLayout.targetKey}:${matchedLayout.disciplineKey ?? DEFAULT_USER_LAYOUT_DISCIPLINE_KEY}:${matchedLayout.document.meta.id}:${matchedLayout.document.meta.updatedAt}`
    : null;

  useEffect(() => {
    if (!targetKey || !matchedLayout || !matchedLayoutKey) {
      resolvedLayoutKeyRef.current = null;
      setCustomLayout(null);
      setLayoutError(null);
      setLayoutLoading(false);
      return;
    }

    if (resolvedLayoutKeyRef.current === matchedLayoutKey) {
      return;
    }

    setLayoutError(null);

    if (matchedLayout.document.meta.type !== layoutType) {
      resolvedLayoutKeyRef.current = matchedLayoutKey;
      setCustomLayout(null);
      setLayoutError("LAYOUT_TYPE_MISMATCH");
      setLayoutLoading(false);
      return;
    }

    const validation = validateLayout(matchedLayout.document);
    if (!validation.valid) {
      resolvedLayoutKeyRef.current = matchedLayoutKey;
      setCustomLayout(null);
      setLayoutError(validation.errors[0] ?? "INVALID_LAYOUT_DOCUMENT");
      setLayoutLoading(false);
      return;
    }

    resolvedLayoutKeyRef.current = matchedLayoutKey;
    setCustomLayout(matchedLayout.document);
    setLayoutLoading(false);
  }, [layoutType, matchedLayout, matchedLayoutKey, targetKey]);

  const loadLayoutFontFaces = useCallback<GetLayoutFontFaces>(async () => {
    if (!fonts.length) return null;
    return {
      apiUrl: websocketUrl,
      fonts,
    };
  }, [fonts, websocketUrl]);

  return {
    customLayout,
    currentThemeName,
    targetLabel: matchedLayout?.targetLabel ?? null,
    isLoading: isLoading || layoutLoading,
    error: layoutError ?? error,
    retry,
    loadLayoutFontFaces,
  };
}
